import { Inject, Injectable, ElementRef } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { environment as ENV } from '@environment';
import { IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { HEADER_TITLE_HEIGHT } from '@app/modules/core/constants/dom.constants';
import { User } from '@app/modules/core/interfaces/user';
import { CountriesVm } from '@booking/entities/countries';
import { INVALID } from '@booking/constants/forms.constants';
import { BillingInfo } from '@billing/entities/billing.payload';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { replaceDiacritics } from '@shared/directives/mappers/replace-diacritics';
import { UserGender } from '@booking/entities/user-gender';
import { getPaxGender } from '@pax/mappers/pax-gender.mapper';

@Injectable()
export class FormInputsService {

  constructor(
    private translate: TranslocoService,
    private viewportScroller: ViewportScroller,
    private scrollFormService: ScrollFormService,
    @Inject(IS_MOBILE) public isMobile: boolean,
  ) { }

  public userFirstName(user: User | BillingInfo): string {
    return user && user.first_name ? replaceDiacritics(user.first_name.split(' ').slice(0, 1).join(' ')) : '';
  }

  public userSecondName(user: User | BillingInfo): string {
    return user && user.first_name ? replaceDiacritics(user.first_name.split(' ').slice(1).join(' ')) : '';
  }

  public getTranslate(key: string | string[]): string | string[] {
    return this.translate.translate(key);
  }

  public scroll(elementRef: ElementRef): void {
    if (this.isMobile && elementRef && elementRef.nativeElement) {
      this.viewportScroller.scrollToPosition([0, elementRef.nativeElement.offsetTop - HEADER_TITLE_HEIGHT]);
    }
  }

  public scrollIntoView(elementRef: ElementRef): void {
    if (this.isMobile && elementRef && elementRef.nativeElement) {
      this.scrollFormService.scrollIntoView(elementRef);
    }
  }

  public countryNames(countries: CountriesVm[]): string[] {
    const baseCountry = countries.find(x => x.code === ENV.countryInfo.iso_code);
    const countriesOrder = countries.map(data => data.name)
      .sort((a, b) => a.localeCompare(b))
      .filter(x => x !== baseCountry?.name);
    if (baseCountry) countriesOrder.unshift(baseCountry.name);
    return countriesOrder;
  }

  public capitalizeValue(value: string): string {
    return isNullOrUndefined(value) ? value : value.toLowerCase().split(' ')
      .map(v => v !== '' ? v[0].toUpperCase() + v.substr(1) : '').join(' ');
  }

  public userGender(gender: string): UserGender {
    return getPaxGender(gender);
  }

  public addErrorClass(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): { [key: string]: boolean } {
    return this.displayFieldCss(field, formValidation, isSubmitted);
  }

  public isFieldInvalid(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): boolean {
    return formValidation && formValidation.get(field) ?
      (!formValidation.get(field).valid || formValidation.get(field).status === INVALID) && isSubmitted : false;
  }

  private displayFieldCss(field: string, formValidation: UntypedFormGroup, isSubmitted: boolean): { [key: string]: boolean } {
    return { error: this.isFieldInvalid(field, formValidation, isSubmitted) };
  }

}
